/**
 * Recursively checks whether a Formio JSON schema contains any component
 * with `validate.required === true`.
 *
 * Covers all standard Formio container types:
 *  - `components[]`  — panels, fieldsets, wells, tabs, nested forms
 *  - `columns[]`     — column layout (each column has its own `components[]`)
 *  - `rows[][]`      — table layout  (each cell has its own `components[]`)
 */
export function hasRequiredFields(schema: unknown): boolean {
  if (!schema || typeof schema !== 'object') return false;

  const obj = schema as Record<string, unknown>;

  // Check this component's own validation
  const validate = obj.validate as Record<string, unknown> | undefined;
  if (validate?.required === true) return true;

  // Recurse into standard components array
  const components = obj.components as unknown[] | undefined;
  if (Array.isArray(components)) {
    for (const comp of components) {
      if (hasRequiredFields(comp)) return true;
    }
  }

  // Recurse into columns (each column object has a `components` array)
  const columns = obj.columns as unknown[] | undefined;
  if (Array.isArray(columns)) {
    for (const col of columns) {
      if (hasRequiredFields(col)) return true;
    }
  }

  // Recurse into table rows (array of arrays, each cell has `components`)
  const rows = obj.rows as unknown[] | undefined;
  if (Array.isArray(rows)) {
    for (const row of rows) {
      if (Array.isArray(row)) {
        for (const cell of row) {
          if (hasRequiredFields(cell)) return true;
        }
      }
    }
  }

  return false;
}
